import React, { useState, useEffect } from 'react';
import {
  Phone,
  ShoppingBag,
  Menu,
  X,
  Sparkles,
  Calendar,
  MessageCircle,
  ChevronRight,
  ChevronDown,
  CalendarCheck
} from 'lucide-react';
import { CONTACT_INFO } from '../data/services';
import { navigateTo, AppRoute } from '../utils/router';
import { LocationPill } from './LocationPill';
import { UserLocationState } from '../services/locationService';
import { getBundlesList, subscribeToCatalogUpdates, syncBundlesFromRemote, BundleItem } from '../services/catalogService';

interface NavbarProps {
  onOpenBookingModal?: () => void;
  onOpenMyBookings?: () => void;
  cartCount?: number;
  currentRoute?: AppRoute;
  userLocation?: UserLocationState | null;
  onOpenLocationPicker?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  onOpenBookingModal: _onOpenBookingModal,
  onOpenMyBookings,
  cartCount = 0,
  currentRoute = 'home',
  userLocation = null,
  onOpenLocationPicker
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bundlesDropdownOpen, setBundlesDropdownOpen] = useState(false);
  const [mobileBundlesExpanded, setMobileBundlesExpanded] = useState(true);
  const [bundlesList, setBundlesList] = useState<BundleItem[]>(() => getBundlesList());

  useEffect(() => {
    syncBundlesFromRemote().catch(() => {});
    const unsubscribe = subscribeToCatalogUpdates(() => {
      setBundlesList(getBundlesList());
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent background scrolling when mobile menu drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);


  return (
    <>
      <header className={`navbar-wrapper ${scrolled ? 'navbar-scrolled' : ''}`}>
        <nav className="navbar" aria-label="Main Navigation">

          {/* Brand Mark */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigateTo('/');
            }}
            className="nav-brand-container"
            aria-label="Blishka Home"
          >
            <div className="brand-logo-mark">
              <img src="/brand-logo.png" alt="Blishka Brand Icon" className="brand-logo-img" />
            </div>
            <div className="brand-text-block">
              <span className="nav-brand-title">Blishka</span>
              <span className="nav-brand-tagline">blissfully you!!</span>
            </div>
          </a>

          {/* Hyperlocal 5 km Service Area Pill */}
          {onOpenLocationPicker && (
            <div className="navbar-location-pill-wrap">
              <LocationPill location={userLocation} onClick={onOpenLocationPicker} />
            </div>
          )}

          {/* Desktop Nav Links */}
          <ul className="nav-links-desktop">
            <li
              className="nav-item-dropdown-wrap"
              onMouseEnter={() => setBundlesDropdownOpen(true)}
              onMouseLeave={() => setBundlesDropdownOpen(false)}
            >
              <a
                href="/bundles"
                onClick={(e) => {
                  e.preventDefault();
                  navigateTo('/bundles');
                  setBundlesDropdownOpen(false);
                }}
                className={`nav-item nav-item-has-dropdown ${currentRoute === 'bundles' ? 'active' : ''}`}
                aria-haspopup="true"
                aria-expanded={bundlesDropdownOpen}
              >
                <span>Bundles</span>
                <ChevronDown
                  size={13}
                  className={`nav-dropdown-chevron ${bundlesDropdownOpen ? 'open' : ''}`}
                />
              </a>

              {/* Desktop Mega Dropdown Flyout */}
              <div
                className={`nav-dropdown-menu ${bundlesDropdownOpen ? 'visible' : ''}`}
                role="menu"
                aria-label="Bundle Packages"
              >
                <div className="nav-dropdown-header">
                  <div className="nav-dropdown-header-title">
                    <Sparkles size={13} color="var(--blishka-berry)" />
                    <span>Curated Combos</span>
                  </div>
                  <span className="nav-dropdown-save-badge">Save up to 25%</span>
                </div>

                <div className="nav-dropdown-bundles-list">
                  {bundlesList.map((bundle) => (
                    <a
                      key={bundle.id}
                      href={`/bundles?package=${bundle.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setBundlesDropdownOpen(false);
                        navigateTo('/bundles', { packageId: bundle.id });
                      }}
                      className="nav-dropdown-bundle-item"
                    >
                      <div className="nav-dropdown-bundle-left">
                        <div className="nav-dropdown-bundle-title-row">
                          <span className="nav-dropdown-bundle-name">{bundle.name}</span>
                          <span className="nav-dropdown-bundle-save">Save ₹{bundle.saveAmount}</span>
                        </div>
                        <span className="nav-dropdown-bundle-services">
                          {bundle.servicesIncluded.slice(0, 3).join(' · ')}
                          {bundle.servicesIncluded.length > 3 ? ` +${bundle.servicesIncluded.length - 3}` : ''}
                        </span>
                      </div>
                      <div className="nav-dropdown-bundle-right">
                        <span className="nav-dropdown-bundle-price">₹{bundle.price}</span>
                        <span className="nav-dropdown-bundle-orig">₹{bundle.originalValue}</span>
                      </div>
                    </a>
                  ))}
                </div>

                <div className="nav-dropdown-footer">
                  <a
                    href="/bundles"
                    onClick={(e) => {
                      e.preventDefault();
                      setBundlesDropdownOpen(false);
                      navigateTo('/bundles');
                    }}
                    className="nav-dropdown-all-link"
                  >
                    <span>View All Packages with Free Visit</span>
                    <ChevronRight size={13} />
                  </a>
                </div>
              </div>
            </li>
            <li>
              <a
                href="/services"
                onClick={(e) => {
                  e.preventDefault();
                  navigateTo('/services');
                }}
                className={`nav-item ${currentRoute === 'services' ? 'active' : ''}`}
              >
                Services
              </a>
            </li>
            <li>
              <a
                href="#why-us"
                onClick={(e) => {
                  e.preventDefault();
                  if (currentRoute !== 'home') {
                    navigateTo('/', { scrollToTop: false });
                    setTimeout(() => {
                      document.getElementById('why-us')?.scrollIntoView({ behavior: 'smooth' });
                    }, 150);
                  } else {
                    document.getElementById('why-us')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="nav-item"
              >
                Why Blishka?
              </a>
            </li>
            <li>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  if (currentRoute !== 'home') {
                    navigateTo('/', { scrollToTop: false });
                    setTimeout(() => {
                      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                    }, 150);
                  } else {
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="nav-item"
              >
                Contact
              </a>
            </li>
            {onOpenMyBookings && (
              <li>
                <button
                  type="button"
                  onClick={() => navigateTo('/my-bookings')}
                  className={`nav-item nav-item-btn ${currentRoute === 'my-bookings' ? 'active' : ''}`}
                  title="View your doorstep bookings"
                >
                  My Bookings
                </button>
              </li>
            )}
          </ul>

          {/* Actions */}
          <div className="nav-actions">
            {/* Call button */}
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              className="nav-call-btn"
              title="Call Blishka"
              aria-label="Call us"
            >
              <Phone size={15} />
              <span className="nav-btn-text">Call</span>
            </a>

            {/* Cart Button with Live Counter Badge */}
            <button
              onClick={() => navigateTo('/cart')}
              className={`nav-cart-btn ${currentRoute === 'cart' ? 'active' : ''}`}
              title="View Cart & Schedule"
              aria-label="View Cart"
            >
              <ShoppingBag size={16} />
              <span className="nav-btn-text">Cart</span>
              {cartCount > 0 && <span className="nav-cart-badge">{cartCount}</span>}
            </button>

            {/* Desktop Book Now Button */}
            <button
              onClick={() => navigateTo('/services')}
              className="btn btn-berry btn-sm nav-desktop-book-btn"
              id="nav-book-btn"
            >
              Book Now
            </button>

            {/* Mobile Hamburger Toggle Button */}
            <button
              type="button"
              className="mobile-menu-toggle-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Navigation Drawer & Backdrop */}
      {mobileMenuOpen && (
        <div
          className="mobile-nav-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`mobile-nav-drawer ${mobileMenuOpen ? 'open' : ''}`}
        aria-label="Mobile Navigation Menu"
        aria-hidden={!mobileMenuOpen}
      >
        <div className="mobile-nav-header">
          <div className="nav-brand-container">
            <div className="brand-logo-mark">
              <img src="/brand-logo.png" alt="Blishka Brand Icon" className="brand-logo-img" />
            </div>
            <div className="brand-text-block">
              <span className="nav-brand-title">Blishka</span>
              <span className="nav-brand-tagline">blissfully you!!</span>
            </div>
          </div>

          <button
            type="button"
            className="mobile-nav-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mobile-nav-body">
          {onOpenLocationPicker && (
            <div className="mobile-drawer-location-wrap">
              <LocationPill
                location={userLocation}
                onClick={() => {
                  setMobileMenuOpen(false);
                  onOpenLocationPicker();
                }}
              />
            </div>
          )}

          {/* Nav Links */}
          <ul className="mobile-nav-list">
            {onOpenMyBookings && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onOpenMyBookings();
                  }}
                  className="mobile-nav-link mobile-nav-btn-link"
                >
                  <span className="mobile-nav-link-left">
                    <CalendarCheck size={18} className="mobile-nav-link-icon" style={{ color: 'var(--blishka-berry)' }} />
                    <span style={{ fontWeight: 700, color: 'var(--blishka-berry)' }}>My Bookings</span>
                  </span>
                  <span className="mobile-nav-track-badge">Track</span>
                </button>
              </li>
            )}
            <li className="mobile-nav-bundle-group">
              <div className="mobile-nav-bundle-header">
                <a
                  href="/bundles"
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileMenuOpen(false);
                    navigateTo('/bundles');
                  }}
                  className={`mobile-nav-link ${currentRoute === 'bundles' ? 'active' : ''}`}
                  style={{ flex: 1 }}
                >
                  <span className="mobile-nav-link-left">
                    <Sparkles size={18} className="mobile-nav-link-icon" />
                    <span>Special Bundles</span>
                  </span>
                </a>
                <button
                  type="button"
                  className={`mobile-nav-accordion-btn ${mobileBundlesExpanded ? 'expanded' : ''}`}
                  onClick={() => setMobileBundlesExpanded(!mobileBundlesExpanded)}
                  aria-label="Toggle bundles package list"
                  aria-expanded={mobileBundlesExpanded}
                >
                  <ChevronDown size={17} />
                </button>
              </div>

              {/* Mobile Sublist of Bundles */}
              {mobileBundlesExpanded && (
                <div className="mobile-nav-bundles-sublist">
                  {bundlesList.map((bundle) => (
                    <a
                      key={bundle.id}
                      href={`/bundles?package=${bundle.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setMobileMenuOpen(false);
                        navigateTo('/bundles', { packageId: bundle.id });
                      }}
                      className="mobile-bundle-subitem"
                    >
                      <div className="mobile-bundle-subitem-left">
                        <span className="mobile-bundle-subitem-title">{bundle.name}</span>
                        <span className="mobile-bundle-subitem-desc">
                          {bundle.servicesIncluded.slice(0, 2).join(' · ')}
                          {bundle.servicesIncluded.length > 2 ? '...' : ''}
                        </span>
                      </div>
                      <div className="mobile-bundle-subitem-right">
                        <span className="mobile-bundle-subitem-price">₹{bundle.price}</span>
                        <span className="mobile-bundle-subitem-save">Save ₹{bundle.saveAmount}</span>
                      </div>
                    </a>
                  ))}
                  <a
                    href="/bundles"
                    onClick={(e) => {
                      e.preventDefault();
                      setMobileMenuOpen(false);
                      navigateTo('/bundles');
                    }}
                    className="mobile-bundle-view-all"
                  >
                    <span>Explore All Combo Packages →</span>
                  </a>
                </div>
              )}
            </li>
            <li>
              <a
                href="/services"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  navigateTo('/services');
                }}
                className={`mobile-nav-link ${currentRoute === 'services' ? 'active' : ''}`}
              >
                <span className="mobile-nav-link-left">
                  <Calendar size={18} className="mobile-nav-link-icon" />
                  <span>Services</span>
                </span>
                <ChevronRight size={16} className="mobile-nav-link-arrow" />
              </a>
            </li>
            <li>
              <a
                href="#why-us"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  if (currentRoute !== 'home') {
                    navigateTo('/', { scrollToTop: false });
                    setTimeout(() => {
                      document.getElementById('why-us')?.scrollIntoView({ behavior: 'smooth' });
                    }, 150);
                  } else {
                    document.getElementById('why-us')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="mobile-nav-link"
              >
                <span className="mobile-nav-link-left">
                  <Sparkles size={18} className="mobile-nav-link-icon" />
                  <span>Why Blishka?</span>
                </span>
                <ChevronRight size={16} className="mobile-nav-link-arrow" />
              </a>
            </li>
            <li>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  if (currentRoute !== 'home') {
                    navigateTo('/', { scrollToTop: false });
                    setTimeout(() => {
                      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                    }, 150);
                  } else {
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="mobile-nav-link"
              >
                <span className="mobile-nav-link-left">
                  <Phone size={18} className="mobile-nav-link-icon" />
                  <span>Contact &amp; Location</span>
                </span>
                <ChevronRight size={16} className="mobile-nav-link-arrow" />
              </a>
            </li>
          </ul>

          {/* Quick Actions inside Drawer */}
          <div className="mobile-nav-actions">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                navigateTo('/cart');
              }}
              className="btn btn-berry mobile-nav-book-btn"
            >
              <ShoppingBag size={18} />
              <span>{cartCount > 0 ? `View Cart (${cartCount} item${cartCount > 1 ? 's' : ''})` : 'Book Appointment'}</span>
            </button>

            <div className="mobile-nav-sub-actions">
              <a
                href={`tel:${CONTACT_INFO.phone}`}
                className="mobile-nav-action-chip"
              >
                <Phone size={15} />
                <span>Call Us</span>
              </a>
              <a
                href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent('Hi Blishka! I would like to inquire about home parlour services.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-nav-action-chip whatsapp-chip"
              >
                <MessageCircle size={15} />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
